// Simple configuration based on environment

import { CALENDAR_BOOKING_WIDGET_PRODUCTION_BASE_URL, CALENDAR_BOOKING_WIDGET_STAGING_US_BASE_URL } from './calendar-booking-widget.urls'
import { getGoogleAnalyticsConfig } from './google-analytics.config'
import { type EnvironmentMode, isEnvironmentMode } from './env-mode'
import { getResolvedEnvironment } from './resolved-environment'

export interface FirebaseConfig {
  storageBucket: string
  membershipStorageBucket: string
  cdnUrl: string
  assetsUrl: string
  oldCdnUrl: string
  membershipCdnUrl: string
  assetsDRMBucket: string
}

/**
 * Build-time, non-secret Firebase project fields. Switches between staging and production
 * Firebase projects based on `NUXT_ENV` at build time.
 *
 * The secret fields (`apiKey`, `appId`, `webPushCertificate`) are NOT on this interface —
 * they live on `runtimeConfig.public.firebaseMessaging` and are injected at pod start
 * via `NUXT_PUBLIC_FIREBASE_MESSAGING_*` env vars (see Nuxt 4 runtime config docs).
 */
export interface FirebaseMessagingConfig {
  authDomain: string
  projectId: string
  messagingSenderId: string
}

export interface Config {
  mode: EnvironmentMode

  // Feature Flags
  SENTRY_ENABLED: boolean
  ANALYTICS_ENABLED: boolean

  // Location IDs
  GOKOLLAB_LOCATION_ID: string

  // Domains
  GOKOLLAB_DOMAIN: string
  GOKOLLAB_MOBILE_DOMAIN: string

  // API URLs
  REST_API_URLS: string
  COMMUNITIES_SERVER_URL: string
  GOKOLLAB_SERVER_URL: string
  COMMUNITIES_SOCKETS_SERVER_URL: string
  MEMBERSHIPS_SERVER_URL: string
  MEMBERSHIP_SERVER_URL: string
  CERTIFICATES_SERVER_URL: string
  CLIENTPORTAL_SERVER_URL: string
  /** Direct clientportal-analytics service (pendo page-hits) — bypasses the /clientclub middleware. */
  CLIENTPORTAL_ANALYTICS_SERVER_URL: string
  CLIENT_PORTAL_SSO_URL: string

  // Storage Buckets
  COMMUNITIES_STORAGE_BUCKET: string
  CLIENT_PORTAL_STORAGE_BUCKET: string
  ASSETS_DRM_BUCKET: string

  // CDN URLs
  COMMUNITIES_CDN_URL: string
  CLIENT_PORTAL_CDN_URL: string

  // Assets DRM URLs
  ASSETS_DRM_IMAGE_URL: string
  ASSETS_DRM_API_URL: string

  // Payment config
  PAYMENT_BASE_URL: string
  TEST_PAYMENTS_PMC_ID: string
  LIVE_PAYMENTS_PMC_ID: string

  GOKOLLAB_ASSETS_BUCKET_URL: string
  GOKOLLAB_STORAGE_BUCKET: string

  FIREBASE: FirebaseConfig
  FIREBASE_MESSAGING: FirebaseMessagingConfig

  // Preview URLs
  CERTIFICATES_PREVIEW_URL: string
  FUNNEL_PREVIEW_BASE_URL: string
  CALENDAR_BOOKING_WIDGET_BASE_URL: string
  FORM_EMBED_SCRIPT_URL: string

  // Google Analytics Configuration
  GOOGLE_ANALYTICS: {
    CLIENTPORTAL: string
    GOKOLLAB: string
    GOKOLLAB_CP_STREAM: string
  }

  KOLLAB_GOOGLE_CLIENT_ID: string

  // Sentry Configuration
  SENTRY_DSN: {
    CLIENTPORTAL: string
    GOKOLLAB: string
    COURSES: string
  }
  SENTRY_ORG: string
  SENTRY_CONFIG: {
    CLIENTPORTAL: {
      org: string
      project: string
      authToken: string
    }
    GOKOLLAB: {
      org: string
      project: string
      authToken: string
    }
    COURSES: {
      org: string
      project: string
      authToken: string
    }
  }
  SENTRY_CLIENT: {
    sampleRate: number
    tracesSampleRate: number
    profilesSampleRate: number
  }
  SENTRY_SERVER: {
    sampleRate: number
    tracesSampleRate: number
  }
  STRIPE_PUBLISHABLE_KEY: string
}

/**
 * Staging Firebase project (non-secret fields).
 * Secrets (apiKey, appId, webPushCertificate) come from runtime via
 * `NUXT_PUBLIC_FIREBASE_MESSAGING_*` env vars on `runtimeConfig.public.firebaseMessaging`.
 */
const firebaseMessagingStagingProject: FirebaseMessagingConfig = {
  authDomain: 'highlevel-staging.firebaseapp.com',
  projectId: 'highlevel-staging',
  messagingSenderId: '85350210461',
}

/**
 * Production Firebase project (non-secret fields).
 * Leave `NUXT_PUBLIC_FIREBASE_MESSAGING_WEB_PUSH_CERTIFICATE` unset in production to use
 * Firebase's default VAPID key in `getToken()`.
 */
const firebaseMessagingProductionProject: FirebaseMessagingConfig = {
  authDomain: 'highlevel-backend.firebaseapp.com',
  projectId: 'highlevel-backend',
  messagingSenderId: '439472444885',
}

export const coursesFirebaseStagingConfig: FirebaseConfig = {
  storageBucket: 'highlevel-staging.appspot.com',
  membershipStorageBucket: 'revex-membership-staging',
  cdnUrl: 'https://staging.cdn.apisystem.tech',
  assetsUrl: 'https://staging.cdn.apisystem.tech',
  oldCdnUrl: 'https://staging.cdn.msgsndr.com',
  membershipCdnUrl: 'https://staging.cdnmembership.apisystem.tech',
  assetsDRMBucket: 'revex-assets-drm-staging',
}

export const coursesFirebaseProductionConfig: FirebaseConfig = {
  storageBucket: 'highlevel-backend.appspot.com',
  membershipStorageBucket: 'revex-membership-production',
  cdnUrl: 'https://cdn.apisystem.tech',
  assetsUrl: 'https://staging.cdn.apisystem.tech',
  oldCdnUrl: 'https://cdn.msgsndr.com',
  membershipCdnUrl: 'https://cdn.courses.apisystem.tech',
  assetsDRMBucket: 'revex-assets-drm-production',
}

function getEnvironment(): EnvironmentMode {
  // Get environment - read from the same source that useEnv() uses: runtimeConfig.public.appEnv
  //
  // Note: We can't call useEnv() directly here because:
  // 1. This function is called at module initialization (export const config = getConfig())
  // 2. useEnv() is a composable that requires Nuxt context, which may not be available at module init
  // 3. This is a regular function, not a composable, so we can't use composables directly
  //
  // Instead, the `resolve-environment` plugin reads `useRuntimeConfig().public.appEnv` — the
  // same source `useEnv()` uses — and pushes it here via `setResolvedEnvironment()` at
  // `enforce: 'pre'`, before any default-order plugin can observe the fallback. `init-app`
  // repeats the same call at default order for the server path.

  // Resolution order: plugin-supplied override, then `window.__NUXT__` (module-initialization
  // only), then `process.env.NUXT_ENV` on the server. `process.env` is NOT a client source at
  // all — Nuxt's client shim is `{}`, so `process.env.NUXT_ENV` is always `undefined` in the
  // browser even though the build scripts set it.

  // Default fallback
  let env: EnvironmentMode = 'development'

  // 1. The plugin-supplied value is authoritative once boot has run. It comes from
  //    `useRuntimeConfig().public.appEnv`, the same source `useEnv()` reads.
  const pluginResolved = getResolvedEnvironment()
  if (pluginResolved) {
    return pluginResolved
  }

  // 2. Module-initialization fallback. DO NOT DELETE THIS BRANCH.
  //    Nuxt still emits an inline `window.__NUXT__={};window.__NUXT__.config=...` script ahead
  //    of the entry bundle on 4.3.1, and `nuxt:revive-payload:client` only deletes that global
  //    later, at plugin order -30. So the branch is dead *after* boot but live *during* module
  //    evaluation — which is when `export const config` below (and `constants/index.ts`,
  //    `media-layer/utils/url.utils.ts`, `kollab-pro-layer/constants/constant.ts` and both
  //    `sentry.client.config.ts` files) read their frozen snapshot. Removing it would silently
  //    move all of those onto the `development` default, with a green build and green tests.
  if (typeof window !== 'undefined') {
    try {
      // Read from runtimeConfig.public.appEnv (standardized) or environment (backwards compat)
      // This is the same data that useEnv() would return via useRuntimeConfig().public.appEnv
      const nuxtConfig = (window as { __NUXT__?: { config?: { public?: { appEnv?: string; environment?: string } } } }).__NUXT__
      const windowEnv = nuxtConfig?.config?.public?.appEnv || nuxtConfig?.config?.public?.environment
      // Only use windowEnv if it's a valid environment value
      if (windowEnv && isEnvironmentMode(windowEnv)) {
        env = windowEnv
        return env // Return immediately if we got a valid value from window.__NUXT__
      }
    } catch (error) {
      // A malformed payload must not take the app down this early, but it must not pass
      // unnoticed either: resolution now falls through to the `development` default.
      console.warn('[Config] Could not read the environment from window.__NUXT__:', error)
    }
  }

  // On server-side, read from process.env.NUXT_ENV
  // IMPORTANT: NUXT_ENV must be set both at build time AND runtime:
  //   Build:   NUXT_ENV=production pnpm build
  //   Runtime: NUXT_ENV=production node .output/server/index.mjs
  // Use the "start" or "start:staging" scripts in package.json to ensure this is set correctly
  if (typeof process !== 'undefined' && process.env?.NUXT_ENV) {
    const processEnv = process.env.NUXT_ENV
    if (processEnv && isEnvironmentMode(processEnv)) {
      env = processEnv
    }
  }

  return env
}

export function getConfig(): Config {
  // Always get environment fresh (not cached) to ensure we read the current NUXT_ENV
  const env = getEnvironment()

  const isSSR = process.server || (typeof window === 'undefined' && typeof global !== 'undefined')

  // Flag to enable internal URLs in development (when VPN is connected)
  const useInternalInDev = false

  // Base configuration
  const config: Config = {
    mode: 'development',

    // Feature Flags (disabled in development)
    SENTRY_ENABLED: false,
    ANALYTICS_ENABLED: false,
    KOLLAB_GOOGLE_CLIENT_ID: '548660082635-bvh6gae5e8om7ej9vkls4grkgee77mav.apps.googleusercontent.com',
    // Default values (development)
    GOKOLLAB_LOCATION_ID: 'bb1S8LPFpG3PEWJBMaNm',
    GOKOLLAB_DOMAIN: 'staging.gokollab.com',
    GOKOLLAB_MOBILE_DOMAIN: 'staging.m.gokollab.com',
    // Server URLs - use internal URLs during SSR when not in development (or when VPN flag is enabled)
    GOKOLLAB_SERVER_URL:
      isSSR && (env !== 'development' || useInternalInDev)
        ? 'https://staging.services.leadconnectorhq.com/gokollab'
        : 'https://staging.services.leadconnectorhq.com/gokollab',
    COMMUNITIES_SERVER_URL:
      isSSR && (env !== 'development' || useInternalInDev)
        ? 'https://staging.services.leadconnectorhq.com/communities'
        : 'https://staging.services.leadconnectorhq.com/communities',
    COMMUNITIES_SOCKETS_SERVER_URL:
      isSSR && (env !== 'development' || useInternalInDev)
        ? 'https://staging.services.leadconnectorhq.com'
        : 'https://staging.services.leadconnectorhq.com',
    CLIENTPORTAL_SERVER_URL:
      isSSR && (env !== 'development' || useInternalInDev)
        ? 'https://staging.services.leadconnectorhq.com/clientclub'
        : 'https://staging.services.leadconnectorhq.com/clientclub',
    CLIENTPORTAL_ANALYTICS_SERVER_URL:
      isSSR && (env !== 'development' || useInternalInDev)
        ? 'https://staging.services.leadconnectorhq.com/clientportal-analytics'
        : 'https://staging.services.leadconnectorhq.com/clientportal-analytics',
    MEMBERSHIPS_SERVER_URL:
      isSSR && (env !== 'development' || useInternalInDev)
        ? 'https://staging.services.leadconnectorhq.com/memberships'
        : 'https://staging.services.leadconnectorhq.com/memberships',
    // Singular /membership: IAM v2 admin login-token exchange (`POST /membership/user/v2/login-token`); distinct from plural /memberships courses APIs.
    MEMBERSHIP_SERVER_URL:
      isSSR && (env !== 'development' || useInternalInDev)
        ? 'https://staging.services.leadconnectorhq.com/membership'
        : 'https://staging.services.leadconnectorhq.com/membership',

    CERTIFICATES_SERVER_URL:
      isSSR && (env !== 'development' || useInternalInDev)
        ? 'https://staging.services.leadconnectorhq.com/certificates'
        : 'https://staging.services.leadconnectorhq.com/certificates',

    REST_API_URLS:
      isSSR && (env !== 'development' || useInternalInDev)
        ? 'https://staging.services.leadconnectorhq.com'
        : 'https://staging.services.leadconnectorhq.com',

    CLIENT_PORTAL_SSO_URL: 'https://staging.sso.clientclub.net',
    // Storage Buckets
    COMMUNITIES_STORAGE_BUCKET: 'revex-communities-staging',
    CLIENT_PORTAL_STORAGE_BUCKET: 'revex-client-portal-staging',
    ASSETS_DRM_BUCKET: 'revex-assets-drm-staging',

    // Payment config
    PAYMENT_BASE_URL: 'https://staging.services.leadconnectorhq.com/payments',
    TEST_PAYMENTS_PMC_ID: 'pmc_1Qoe3DFpU9DlKp7RCIeNdgoK',
    LIVE_PAYMENTS_PMC_ID: 'pmc_1QwcZcFpU9DlKp7RZMEJf0m9',

    // Third-party services
    GOKOLLAB_ASSETS_BUCKET_URL: 'https://storage.googleapis.com/revex-gokollab-staging/assets',
    GOKOLLAB_STORAGE_BUCKET: 'revex-gokollab-staging',

    COMMUNITIES_CDN_URL: 'https://cdncommunities.apisystem.tech',
    CLIENT_PORTAL_CDN_URL: 'https://cdnclientportal.apisystem.tech',

    // Assets DRM URLs (staging)
    ASSETS_DRM_IMAGE_URL: 'https://staging.assetsdrm.clientclub.net',
    ASSETS_DRM_API_URL: 'https://staging.backend.leadconnectorhq.com/assets-drm',

    FIREBASE: coursesFirebaseStagingConfig,
    FIREBASE_MESSAGING: firebaseMessagingStagingProject,

    // Preview URLs
    CERTIFICATES_PREVIEW_URL: 'https://staging.my-certificates.com/certificates',
    // Dev-only absolute host — staging/prod/interop use host-relative `/preview`.
    FUNNEL_PREVIEW_BASE_URL: 'https://staging.gokollab.com/preview',
    CALENDAR_BOOKING_WIDGET_BASE_URL: CALENDAR_BOOKING_WIDGET_STAGING_US_BASE_URL,
    FORM_EMBED_SCRIPT_URL: 'https://staging.link.gohighlevel.com/js/form_embed.js',

    // Google Analytics Configuration (resolved per env before return)
    GOOGLE_ANALYTICS: getGoogleAnalyticsConfig('development'),

    // Sentry DSNs (same for all environments)
    SENTRY_DSN: {
      CLIENTPORTAL: 'https://184ac389721d48c40d5ab1b8054e5e2b@o176457.ingest.us.sentry.io/4511704921276420',
      GOKOLLAB: 'https://062e10c48626eac2c10c8c449d2467b7@o176457.ingest.us.sentry.io/4510198087614464',
      COURSES: '',
    },

    // Sentry Organization (same for all apps)
    SENTRY_ORG: 'gohighlevel',

    // Sentry Project Configuration
    SENTRY_CONFIG: {
      CLIENTPORTAL: {
        org: 'gohighlevel',
        project: 'clientportal-nuxt',
        authToken: process.env.SENTRY_AUTH_TOKEN as string,
      },
      GOKOLLAB: {
        org: 'gohighlevel',
        project: 'gokollab-nuxt',
        authToken: process.env.SENTRY_AUTH_TOKEN as string,
      },
      COURSES: {
        org: 'gohighlevel',
        project: 'courses-nuxt',
        authToken: process.env.SENTRY_AUTH_TOKEN as string,
      },
    },

    // Sentry Client Sample Rates (development defaults)
    SENTRY_CLIENT: {
      sampleRate: 1.0,
      tracesSampleRate: 0.5,
      profilesSampleRate: 0.3,
    },

    // Sentry Server Sample Rates (development defaults)
    SENTRY_SERVER: {
      sampleRate: 1.0,
      tracesSampleRate: 1.0,
    },
    STRIPE_PUBLISHABLE_KEY: 'pk_test_WvH5LGch2F7bqyIJ6gVMu3iE',
  }

  // Staging overrides
  if (env === 'staging') {
    config.mode = 'staging'

    // Enable monitoring and analytics in staging
    config.SENTRY_ENABLED = true
    config.ANALYTICS_ENABLED = true

    config.KOLLAB_GOOGLE_CLIENT_ID = '548660082635-bvh6gae5e8om7ej9vkls4grkgee77mav.apps.googleusercontent.com'
    config.GOKOLLAB_SERVER_URL = isSSR
      ? 'https://staging.services.leadconnectorhq.com/gokollab'
      : 'https://staging.services.leadconnectorhq.com/gokollab'
    config.COMMUNITIES_SERVER_URL = isSSR
      ? 'https://staging.services.leadconnectorhq.com/communities'
      : 'https://staging.services.leadconnectorhq.com/communities'
    config.COMMUNITIES_SOCKETS_SERVER_URL = isSSR
      ? 'https://staging.services.leadconnectorhq.com'
      : 'https://staging.services.leadconnectorhq.com'
    config.REST_API_URLS = isSSR ? 'https://staging.services.leadconnectorhq.com' : 'https://staging.services.leadconnectorhq.com'
    config.CLIENTPORTAL_SERVER_URL = isSSR
      ? 'https://staging.services.leadconnectorhq.com/clientclub'
      : 'https://staging.services.leadconnectorhq.com/clientclub'
    config.CLIENTPORTAL_ANALYTICS_SERVER_URL = isSSR
      ? 'https://staging.services.leadconnectorhq.com/clientportal-analytics'
      : 'https://staging.services.leadconnectorhq.com/clientportal-analytics'
    config.MEMBERSHIPS_SERVER_URL = isSSR
      ? 'https://staging.services.leadconnectorhq.com/memberships'
      : 'https://staging.services.leadconnectorhq.com/memberships'
    config.MEMBERSHIP_SERVER_URL = isSSR
      ? 'https://staging.services.leadconnectorhq.com/membership'
      : 'https://staging.services.leadconnectorhq.com/membership'
    config.CERTIFICATES_SERVER_URL = isSSR
      ? 'https://staging.services.leadconnectorhq.com/certificates'
      : 'https://staging.services.leadconnectorhq.com/certificates'
    config.CERTIFICATES_PREVIEW_URL = 'https://staging.my-certificates.com/certificates'
    // Host-relative — resolves to the current portal/custom domain (e.g. staging.gokollab.com/preview/:id).
    config.FUNNEL_PREVIEW_BASE_URL = '/preview'

    // Sentry sample rates for staging
    config.SENTRY_CLIENT = {
      sampleRate: 1.0,
      tracesSampleRate: 0.5,
      profilesSampleRate: 0.3,
    }
    config.SENTRY_SERVER = {
      sampleRate: 1.0,
      tracesSampleRate: 0.5,
    }
    config.STRIPE_PUBLISHABLE_KEY = 'pk_test_WvH5LGch2F7bqyIJ6gVMu3iE'
  }

  // Interop overrides
  if (env === 'interop') {
    config.mode = 'interop'

    // Enable monitoring and analytics in interop
    config.SENTRY_ENABLED = true
    config.ANALYTICS_ENABLED = true

    config.KOLLAB_GOOGLE_CLIENT_ID = '548660082635-bvh6gae5e8om7ej9vkls4grkgee77mav.apps.googleusercontent.com'
    config.GOKOLLAB_LOCATION_ID = 'VyoxA4YGXCZib071EFsR'
    config.GOKOLLAB_DOMAIN = 'interop.gokollab.com'
    config.GOKOLLAB_MOBILE_DOMAIN = 'interop.m.gokollab.com'
    config.COMMUNITIES_SERVER_URL = isSSR
      ? 'https://interop.services.leadconnectorhq.com/communities'
      : 'https://interop.services.leadconnectorhq.com/communities'
    config.COMMUNITIES_SOCKETS_SERVER_URL = isSSR
      ? 'https://interop.services.leadconnectorhq.com'
      : 'https://interop.services.leadconnectorhq.com'
    config.CLIENTPORTAL_SERVER_URL = isSSR
      ? 'https://interop.services.leadconnectorhq.com/clientclub'
      : 'https://interop.services.leadconnectorhq.com/clientclub'
    config.CLIENTPORTAL_ANALYTICS_SERVER_URL = isSSR
      ? 'https://interop.services.leadconnectorhq.com/clientportal-analytics'
      : 'https://interop.services.leadconnectorhq.com/clientportal-analytics'
    config.REST_API_URLS = isSSR ? 'https://interop.services.leadconnectorhq.com' : 'https://interop.services.leadconnectorhq.com'
    config.GOKOLLAB_SERVER_URL = isSSR
      ? 'https://interop.services.leadconnectorhq.com/gokollab'
      : 'https://interop.services.leadconnectorhq.com/gokollab'
    config.MEMBERSHIPS_SERVER_URL = isSSR
      ? 'https://interop.services.leadconnectorhq.com/memberships'
      : 'https://interop.services.leadconnectorhq.com/memberships'
    config.MEMBERSHIP_SERVER_URL = isSSR
      ? 'https://interop.services.leadconnectorhq.com/membership'
      : 'https://interop.services.leadconnectorhq.com/membership'
    config.CERTIFICATES_SERVER_URL = isSSR
      ? 'https://interop.services.leadconnectorhq.com/certificates'
      : 'https://interop.services.leadconnectorhq.com/certificates'
    config.COMMUNITIES_CDN_URL = 'https://interop.cdncommunities.apisystem.tech'
    config.CLIENT_PORTAL_CDN_URL = 'https://interop.cdnclientportal.apisystem.tech'
    config.PAYMENT_BASE_URL = 'https://interop.services.leadconnectorhq.com/payments'
    config.ASSETS_DRM_BUCKET = 'revex-assets-drm-production'
    config.ASSETS_DRM_IMAGE_URL = 'https://assetsdrm.clientclub.net'
    config.ASSETS_DRM_API_URL = 'https://backend.leadconnectorhq.com/assets-drm'
    config.GOKOLLAB_ASSETS_BUCKET_URL = 'https://storage.googleapis.com/revex-gokollab-production/assets'
    config.GOKOLLAB_STORAGE_BUCKET = 'revex-gokollab-production'
    config.FIREBASE = coursesFirebaseProductionConfig
    config.FIREBASE_MESSAGING = firebaseMessagingProductionProject
    config.CERTIFICATES_PREVIEW_URL = 'https://my-certificates.com/certificates'
    // Host-relative — resolves to the current portal/custom domain.
    config.FUNNEL_PREVIEW_BASE_URL = '/preview'
    config.FORM_EMBED_SCRIPT_URL = 'https://link.msgsndr.com/js/form_embed.js'

    // Sentry sample rates for interop
    config.SENTRY_CLIENT = {
      sampleRate: 1.0,
      tracesSampleRate: 0.5,
      profilesSampleRate: 0.3,
    }
    config.SENTRY_SERVER = {
      sampleRate: 1.0,
      tracesSampleRate: 0.5,
    }
    config.STRIPE_PUBLISHABLE_KEY = 'pk_test_WvH5LGch2F7bqyIJ6gVMu3iE'
  }

  // Production overrides
  if (env === 'production') {
    config.mode = 'production'

    // Enable monitoring and analytics in production
    config.SENTRY_ENABLED = true
    config.ANALYTICS_ENABLED = true
    config.KOLLAB_GOOGLE_CLIENT_ID = '548660082635-l46f0u8vlh1pioo8nbvall05c9drilkk.apps.googleusercontent.com'
    config.GOKOLLAB_LOCATION_ID = 'VyoxA4YGXCZib071EFsR'
    config.GOKOLLAB_DOMAIN = 'gokollab.com'
    config.GOKOLLAB_MOBILE_DOMAIN = 'm.gokollab.com'
    config.REST_API_URLS = 'https://services.leadconnectorhq.com'
    config.COMMUNITIES_SERVER_URL = isSSR
      ? 'https://services.leadconnectorhq.com/communities'
      : 'https://services.leadconnectorhq.com/communities'
    config.COMMUNITIES_SOCKETS_SERVER_URL = isSSR ? 'https://services.leadconnectorhq.com' : 'https://services.leadconnectorhq.com'
    config.CLIENTPORTAL_SERVER_URL = isSSR
      ? 'https://services.leadconnectorhq.com/clientclub'
      : 'https://services.leadconnectorhq.com/clientclub'
    config.CLIENTPORTAL_ANALYTICS_SERVER_URL = isSSR
      ? 'https://services.leadconnectorhq.com/clientportal-analytics'
      : 'https://services.leadconnectorhq.com/clientportal-analytics'
    config.GOKOLLAB_SERVER_URL = isSSR ? 'https://services.leadconnectorhq.com/gokollab' : 'https://services.leadconnectorhq.com/gokollab'
    config.MEMBERSHIPS_SERVER_URL = isSSR
      ? 'https://services.leadconnectorhq.com/memberships'
      : 'https://services.leadconnectorhq.com/memberships'
    config.MEMBERSHIP_SERVER_URL = isSSR
      ? 'https://services.leadconnectorhq.com/membership'
      : 'https://services.leadconnectorhq.com/membership'
    config.CERTIFICATES_SERVER_URL = isSSR
      ? 'https://services.leadconnectorhq.com/certificates'
      : 'https://services.leadconnectorhq.com/certificates'
    config.COMMUNITIES_STORAGE_BUCKET = 'revex-communities-production'
    config.CLIENT_PORTAL_STORAGE_BUCKET = 'revex-client-portal-production'
    config.COMMUNITIES_CDN_URL = 'https://cdncommunities.apisystem.tech'
    config.CLIENT_PORTAL_CDN_URL = 'https://cdnclientportal.apisystem.tech'
    config.PAYMENT_BASE_URL = 'https://services.leadconnectorhq.com/payments'
    config.CLIENT_PORTAL_SSO_URL = 'https://sso.clientclub.net'
    config.ASSETS_DRM_BUCKET = 'revex-assets-drm-production'
    config.ASSETS_DRM_IMAGE_URL = 'https://assetsdrm.clientclub.net'
    config.ASSETS_DRM_API_URL = 'https://backend.leadconnectorhq.com/assets-drm'
    config.GOKOLLAB_ASSETS_BUCKET_URL = 'https://storage.googleapis.com/revex-gokollab-production/assets'
    config.GOKOLLAB_STORAGE_BUCKET = 'revex-gokollab-production'
    config.FIREBASE = coursesFirebaseProductionConfig
    config.FIREBASE_MESSAGING = firebaseMessagingProductionProject
    config.CERTIFICATES_PREVIEW_URL = 'https://my-certificates.com/certificates'
    // Host-relative — resolves to the current portal/custom domain (e.g. gokollab.com/preview/:id).
    config.FUNNEL_PREVIEW_BASE_URL = '/preview'
    config.FORM_EMBED_SCRIPT_URL = 'https://link.msgsndr.com/js/form_embed.js'

    // Sentry sample rates for production (cost-optimized)
    config.SENTRY_CLIENT = {
      sampleRate: 0.5,
      tracesSampleRate: 0.1,
      profilesSampleRate: 0.05,
    }
    config.SENTRY_SERVER = {
      sampleRate: 0.5,
      tracesSampleRate: 0.1,
    }
    config.STRIPE_PUBLISHABLE_KEY = 'pk_live_MtxwO3obi7pfD7UZlGkfR2yj'
  }

  config.GOOGLE_ANALYTICS = getGoogleAnalyticsConfig(env)

  if (env === 'production') {
    config.CALENDAR_BOOKING_WIDGET_BASE_URL = CALENDAR_BOOKING_WIDGET_PRODUCTION_BASE_URL
  }

  return config
}

// Export as a singleton
export const config = getConfig()

// For backwards compatibility if needed
export default config
